import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Trash2, GripVertical, Save, ArrowLeft, Globe, Eye } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TemplatePreview from '../components/TemplatePreview';

const uuid = () => crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9) + Date.now().toString(36);

function SortableQuestion({ q, idx, isSelected, onSelect }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: q.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : 'auto' };
  return (
    <div ref={setNodeRef} style={style} onClick={onSelect} data-testid={`question-item-${idx}`}
      className={`flex items-center gap-2 px-3 py-2 rounded-sm mb-1 cursor-pointer text-sm ${isSelected ? 'bg-zinc-100' : 'hover:bg-zinc-50'}`}>
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-300 hover:text-zinc-500 flex-shrink-0 touch-none">
        <GripVertical size={12} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="truncate text-zinc-800">{q.order}. {q.text}</div>
        <div className="text-[10px] text-zinc-400">{q.type?.replace('_', '/')}</div>
      </div>
    </div>
  );
}

function SortableOption({ opt, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: opt.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : 'auto', position: 'relative' };
  return (
    <div ref={setNodeRef} style={style} className="border border-zinc-100 rounded-sm p-3 mb-2 bg-white">
      <div className="flex items-center gap-2 mb-2">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-300 hover:text-zinc-500 flex-shrink-0 touch-none">
          <GripVertical size={12} />
        </div>
        {children}
      </div>
    </div>
  );
}

const ITEM_TYPES = [
  { value: 'tick', label: 'Tick item' },
  { value: 'note', label: 'Note / reminder' },
  { value: 'write_in', label: 'Write-in field' },
  { value: 'heading', label: 'Section heading' },
];

export default function TemplateEditorPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('questions');
  const [selectedQIdx, setSelectedQIdx] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const load = useCallback(async () => {
    if (!templateId) {
      try {
        const res = await api.post('/templates', { title: 'Untitled Template' });
        navigate(`/admin/templates/${res.data.id}/edit`, { replace: true });
      } catch { navigate('/admin/templates'); }
      return;
    }
    try {
      const res = await api.get(`/templates/${templateId}`);
      setTemplate(res.data);
      setLoading(false);
    } catch { navigate('/admin/templates'); }
  }, [templateId, navigate]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!template) return;
    setSaving(true);
    try {
      const res = await api.put(`/templates/${template.id}`, {
        title: template.title,
        description: template.description,
        questions: template.questions,
        project_fields: template.project_fields,
      });
      setTemplate(res.data);
      setDirty(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    await save();
    try {
      const res = await api.patch(`/templates/${template.id}/status`, { status: 'published' });
      setTemplate(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Publish failed'); }
  };

  const update = (changes) => { setTemplate(prev => ({ ...prev, ...changes })); setDirty(true); };
  const updateQuestion = (idx, changes) => {
    const qs = [...template.questions];
    qs[idx] = { ...qs[idx], ...changes };
    update({ questions: qs });
  };

  const addQuestion = () => {
    const newQ = { id: uuid(), text: 'New question', help_text: '', type: 'yes_no', required: true, order: (template.questions?.length || 0) + 1,
      answer_options: [
        { id: uuid(), label: 'Yes', order: 1, branch_rule: { action: 'next', target_question_id: null }, output_items: [] },
        { id: uuid(), label: 'No', order: 2, branch_rule: { action: 'next', target_question_id: null }, output_items: [] },
      ]
    };
    update({ questions: [...(template.questions || []), newQ] });
    setSelectedQIdx((template.questions || []).length);
  };

  const deleteQuestion = (idx) => {
    const qs = template.questions.filter((_, i) => i !== idx);
    qs.forEach((q, i) => { q.order = i + 1; });
    update({ questions: qs });
    setSelectedQIdx(null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = questions.findIndex(q => q.id === active.id);
    const newIndex = questions.findIndex(q => q.id === over.id);
    const reordered = arrayMove([...questions], oldIndex, newIndex);
    reordered.forEach((q, i) => { q.order = i + 1; });
    update({ questions: reordered });
    if (selectedQIdx === oldIndex) setSelectedQIdx(newIndex);
    else if (selectedQIdx !== null) {
      if (oldIndex < selectedQIdx && newIndex >= selectedQIdx) setSelectedQIdx(selectedQIdx - 1);
      else if (oldIndex > selectedQIdx && newIndex <= selectedQIdx) setSelectedQIdx(selectedQIdx + 1);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const changeQType = (idx, newType) => {
    const q = { ...template.questions[idx] };
    q.type = newType;
    if (newType === 'yes_no') {
      q.answer_options = [
        { id: uuid(), label: 'Yes', order: 1, branch_rule: { action: 'next', target_question_id: null }, output_items: [] },
        { id: uuid(), label: 'No', order: 2, branch_rule: { action: 'next', target_question_id: null }, output_items: [] },
      ];
    } else if (!q.answer_options?.length) {
      q.answer_options = [{ id: uuid(), label: 'Option 1', order: 1, branch_rule: { action: 'next', target_question_id: null }, output_items: [] }];
    }
    updateQuestion(idx, q);
  };

  const addOption = (qIdx) => {
    const q = template.questions[qIdx];
    const opts = [...(q.answer_options || [])];
    opts.push({ id: uuid(), label: `Option ${opts.length + 1}`, order: opts.length + 1, branch_rule: { action: 'next', target_question_id: null }, output_items: [] });
    updateQuestion(qIdx, { answer_options: opts });
  };

  const handleOptionDragEnd = (qIdx, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const q = template.questions[qIdx];
    const opts = [...(q.answer_options || [])];
    const oldIndex = opts.findIndex(o => o.id === active.id);
    const newIndex = opts.findIndex(o => o.id === over.id);
    const reordered = arrayMove(opts, oldIndex, newIndex);
    reordered.forEach((o, i) => { o.order = i + 1; });
    updateQuestion(qIdx, { answer_options: reordered });
  };

  const optionSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const updateOption = (qIdx, oIdx, changes) => {
    const q = template.questions[qIdx];
    const opts = [...q.answer_options];
    opts[oIdx] = { ...opts[oIdx], ...changes };
    updateQuestion(qIdx, { answer_options: opts });
  };

  const deleteOption = (qIdx, oIdx) => {
    const q = template.questions[qIdx];
    const opts = q.answer_options.filter((_, i) => i !== oIdx);
    opts.forEach((o, i) => { o.order = i + 1; });
    updateQuestion(qIdx, { answer_options: opts });
  };

  const addOutputItem = (qIdx, oIdx) => {
    const q = template.questions[qIdx];
    const opt = q.answer_options[oIdx];
    const items = [...(opt.output_items || [])];
    items.push({ id: uuid(), type: 'tick', text: '', sub_note: '', section_group: '', order: items.length + 1 });
    const opts = [...q.answer_options];
    opts[oIdx] = { ...opt, output_items: items };
    updateQuestion(qIdx, { answer_options: opts });
  };

  const updateOutputItem = (qIdx, oIdx, iIdx, changes) => {
    const q = template.questions[qIdx];
    const opt = q.answer_options[oIdx];
    const items = [...opt.output_items];
    items[iIdx] = { ...items[iIdx], ...changes };
    const opts = [...q.answer_options];
    opts[oIdx] = { ...opt, output_items: items };
    updateQuestion(qIdx, { answer_options: opts });
  };

  const deleteOutputItem = (qIdx, oIdx, iIdx) => {
    const q = template.questions[qIdx];
    const opt = q.answer_options[oIdx];
    const items = opt.output_items.filter((_, i) => i !== iIdx);
    const opts = [...q.answer_options];
    opts[oIdx] = { ...opt, output_items: items };
    updateQuestion(qIdx, { answer_options: opts });
  };

  const addProjectField = () => {
    const fields = [...(template.project_fields || [])];
    const key = `custom_${fields.length + 1}`;
    fields.push({ key, label: 'Custom field', placeholder: '', required: false, auto_fill: null, order: fields.length + 1 });
    update({ project_fields: fields });
  };

  const updateProjectField = (idx, changes) => {
    const fields = [...template.project_fields];
    fields[idx] = { ...fields[idx], ...changes };
    update({ project_fields: fields });
  };

  const deleteProjectField = (idx) => {
    const f = template.project_fields[idx];
    if (['client_name', 'project_name', 'engineer_name'].includes(f.key)) return;
    update({ project_fields: template.project_fields.filter((_, i) => i !== idx) });
  };

  if (loading) return <div className="flex items-center justify-center h-full text-sm text-zinc-400">Loading...</div>;
  if (!template) return null;

  const questions = template.questions || [];
  const selQ = selectedQIdx !== null ? questions[selectedQIdx] : null;

  return (
    <div className="h-full flex flex-col" data-testid="template-editor-page">
      <div className="flex items-center justify-between px-6 py-3 border-b border-zinc-200 bg-white">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/admin/templates')} className="text-zinc-400 hover:text-zinc-600 flex-shrink-0"><ArrowLeft size={16} /></button>
          <input value={template.title} onChange={e => update({ title: e.target.value })} data-testid="template-title-input"
            className="text-lg font-bold tracking-tight text-zinc-900 border-none outline-none bg-transparent min-w-0" />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge variant="outline" className={`rounded-sm text-[10px] font-semibold h-8 px-3 flex items-center ${template.status === 'published' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : template.status === 'draft' ? 'bg-zinc-100 text-zinc-600 border-zinc-200' : 'bg-zinc-100 text-zinc-400 border-zinc-200'}`}>{template.status}</Badge>
          <div className="w-px h-6 bg-zinc-200" />
          <Button variant="outline" size="sm" onClick={() => setShowPreview(true)} disabled={!questions.length} data-testid="preview-template-btn" className="rounded-sm text-xs gap-1.5">
            <Eye size={12} /> Preview
          </Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="save-template-btn" className="rounded-sm text-xs gap-1.5"
            style={{ background: 'var(--brand-primary, #1C3557)' }}>
            <Save size={12} /> {saving ? 'Saving...' : 'Save'}
          </Button>
          {template.status === 'draft' && (
            <Button variant="outline" size="sm" onClick={publish} data-testid="publish-template-btn" className="rounded-sm text-xs gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <Globe size={12} /> Publish
            </Button>
          )}
        </div>
      </div>

      <div className="flex border-b border-zinc-100 bg-white px-6">
        {['questions', 'branch_logic', 'output_items', 'project_fields'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px capitalize ${tab === t ? 'border-zinc-800 text-zinc-800' : 'border-transparent text-zinc-400 hover:text-zinc-600'}`}>
            {t.replace('_', ' ')}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-hidden">
        {tab === 'questions' && (
          <div className="flex h-full">
            <div className="w-80 border-r border-zinc-100 overflow-y-auto bg-white p-3">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={questions.map(q => q.id)} strategy={verticalListSortingStrategy}>
                  {questions.map((q, idx) => (
                    <SortableQuestion key={q.id} q={q} idx={idx} isSelected={selectedQIdx === idx} onSelect={() => setSelectedQIdx(idx)} />
                  ))}
                </SortableContext>
              </DndContext>
              <button onClick={addQuestion} data-testid="add-question-btn" className="flex items-center gap-1.5 w-full px-3 py-2 text-xs text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50 rounded-sm mt-1">
                <Plus size={12} /> Add question
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {selQ ? (
                <div>
                  <div className="mb-3">
                    <label className="block text-[11px] uppercase tracking-[0.06em] text-zinc-400 mb-1">Question text</label>
                    <textarea value={selQ.text} onChange={e => updateQuestion(selectedQIdx, { text: e.target.value })} data-testid="question-text-input"
                      className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm resize-none" rows={2} />
                  </div>
                  <div className="mb-3">
                    <label className="block text-[11px] uppercase tracking-[0.06em] text-zinc-400 mb-1">Help text (optional)</label>
                    <input value={selQ.help_text || ''} onChange={e => updateQuestion(selectedQIdx, { help_text: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm" />
                  </div>
                  <div className="mb-4">
                    <label className="block text-[11px] uppercase tracking-[0.06em] text-zinc-400 mb-1">Question type</label>
                    <select value={selQ.type} onChange={e => changeQType(selectedQIdx, e.target.value)} data-testid="question-type-select"
                      className="px-3 py-2 text-sm border border-zinc-200 rounded-sm">
                      <option value="yes_no">Yes / No</option>
                      <option value="single_choice">Single choice</option>
                      <option value="multi_choice">Multi-choice</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <div className="text-[11px] uppercase tracking-[0.06em] text-zinc-400 mb-2">Answer options</div>
                    <DndContext sensors={optionSensors} collisionDetection={closestCenter} onDragEnd={(e) => handleOptionDragEnd(selectedQIdx, e)}>
                      <SortableContext items={(selQ.answer_options || []).map(o => o.id)} strategy={verticalListSortingStrategy}>
                        {(selQ.answer_options || []).map((opt, oIdx) => (
                          <SortableOption key={opt.id} opt={opt}>
                            <input value={opt.label} onChange={e => updateOption(selectedQIdx, oIdx, { label: e.target.value })}
                              className="flex-1 px-2 py-1 text-sm border border-zinc-200 rounded-sm" />
                            {selQ.type !== 'yes_no' && (
                              <button onClick={() => deleteOption(selectedQIdx, oIdx)} className="text-zinc-300 hover:text-red-500"><Trash2 size={12} /></button>
                            )}
                            <div className="w-full mt-2">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-[10px] text-zinc-400 w-16">Branch:</span>
                                <select value={opt.branch_rule?.action || 'next'} onChange={e => updateOption(selectedQIdx, oIdx, { branch_rule: { ...opt.branch_rule, action: e.target.value, target_question_id: e.target.value === 'next' || e.target.value === 'end' ? null : opt.branch_rule?.target_question_id } })}
                                  className="text-xs border border-zinc-200 rounded-sm px-2 py-1">
                                  <option value="next">Next in order</option>
                                  <option value="show_question">Go to question</option>
                                  <option value="skip_to">Skip to question</option>
                                  <option value="end">End run</option>
                                </select>
                                {(opt.branch_rule?.action === 'show_question' || opt.branch_rule?.action === 'skip_to') && (
                                  <select value={opt.branch_rule?.target_question_id || ''} onChange={e => updateOption(selectedQIdx, oIdx, { branch_rule: { ...opt.branch_rule, target_question_id: e.target.value || null } })}
                                    className="text-xs border border-zinc-200 rounded-sm px-2 py-1 flex-1 min-w-0">
                                    <option value="">Select question...</option>
                                    {questions.filter((_, qi) => qi !== selectedQIdx).map(q => (
                                      <option key={q.id} value={q.id}>Q{q.order}: {q.text}</option>
                                    ))}
                                  </select>
                                )}
                              </div>
                              <div className="mt-2">
                                <div className="text-[10px] text-zinc-400 mb-1">Output items:</div>
                                {(opt.output_items || []).map((item, iIdx) => (
                                  <div key={item.id} className="flex items-start gap-2 mb-1.5 pl-2 border-l-2 border-zinc-100">
                                    <select value={item.type} onChange={e => updateOutputItem(selectedQIdx, oIdx, iIdx, { type: e.target.value })}
                                      className="text-[10px] border border-zinc-200 rounded-sm px-1 py-0.5 w-32 flex-shrink-0">
                                      {ITEM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                    <input value={item.text} onChange={e => updateOutputItem(selectedQIdx, oIdx, iIdx, { text: e.target.value })} placeholder="Item text"
                                      className="flex-1 text-xs border border-zinc-200 rounded-sm px-2 py-0.5" />
                                    {item.type === 'tick' && (
                                      <input value={item.sub_note || ''} onChange={e => updateOutputItem(selectedQIdx, oIdx, iIdx, { sub_note: e.target.value })} placeholder="Sub-note"
                                        className="w-32 text-xs border border-zinc-200 rounded-sm px-2 py-0.5" />
                                    )}
                                    <input value={item.section_group || ''} onChange={e => updateOutputItem(selectedQIdx, oIdx, iIdx, { section_group: e.target.value })} placeholder="Group"
                                      className="w-24 text-xs border border-zinc-200 rounded-sm px-2 py-0.5" />
                                    <button onClick={() => deleteOutputItem(selectedQIdx, oIdx, iIdx)} className="text-zinc-300 hover:text-red-500 flex-shrink-0"><Trash2 size={10} /></button>
                                  </div>
                                ))}
                                <button onClick={() => addOutputItem(selectedQIdx, oIdx)} className="text-[10px] text-zinc-400 hover:text-zinc-600 flex items-center gap-1 mt-1">
                                  <Plus size={10} /> Add output item
                                </button>
                              </div>
                            </div>
                          </SortableOption>
                        ))}
                      </SortableContext>
                    </DndContext>
                    {selQ.type !== 'yes_no' && (
                      <button onClick={() => addOption(selectedQIdx)} className="text-xs text-zinc-400 hover:text-zinc-600 flex items-center gap-1 mt-1">
                        <Plus size={12} /> Add option
                      </button>
                    )}
                  </div>

                  <button onClick={() => deleteQuestion(selectedQIdx)} className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-600 mt-4">
                    <Trash2 size={12} /> Delete question
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-zinc-400">
                  Select a question to edit or add a new one
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'branch_logic' && (
          <div className="p-5 overflow-y-auto h-full">
            <h2 className="text-sm font-medium text-zinc-900 mb-3">Branch logic summary</h2>
            {questions.length === 0 ? <p className="text-xs text-zinc-400">No questions yet</p> : (
              <div className="bg-white border border-zinc-100 rounded-sm">
                <table className="w-full text-xs">
                  <thead><tr className="text-[10px] uppercase tracking-[0.06em] text-zinc-400 border-b border-zinc-50">
                    <th className="text-left px-3 py-2 font-medium">Question</th>
                    <th className="text-left px-3 py-2 font-medium">Option</th>
                    <th className="text-left px-3 py-2 font-medium">Action</th>
                    <th className="text-left px-3 py-2 font-medium">Target</th>
                    <th className="text-left px-3 py-2 font-medium">Output items</th>
                  </tr></thead>
                  <tbody>
                    {questions.flatMap((q, qi) => (q.answer_options || []).map((opt, oi) => {
                      const target = opt.branch_rule?.target_question_id ? questions.find(tq => tq.id === opt.branch_rule.target_question_id) : null;
                      return (
                        <tr key={`${qi}-${oi}`} className="border-b border-zinc-50">
                          <td className="px-3 py-1.5 text-zinc-700">{oi === 0 ? `Q${q.order}: ${q.text?.slice(0, 30)}` : ''}</td>
                          <td className="px-3 py-1.5 text-zinc-500">{opt.label}</td>
                          <td className="px-3 py-1.5 text-zinc-500">{opt.branch_rule?.action}</td>
                          <td className="px-3 py-1.5 text-zinc-500">{target ? `Q${target.order}` : '-'}</td>
                          <td className="px-3 py-1.5 text-zinc-400">{(opt.output_items || []).length}</td>
                        </tr>
                      );
                    }))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'output_items' && (
          <div className="p-5 overflow-y-auto h-full">
            <h2 className="text-sm font-medium text-zinc-900 mb-3">All output items</h2>
            {questions.length === 0 ? <p className="text-xs text-zinc-400">No questions yet</p> : (
              <div className="space-y-3">
                {questions.map((q, qi) => (q.answer_options || []).filter(o => o.output_items?.length > 0).map((opt, oi) => (
                  <div key={`${qi}-${oi}`} className="bg-white border border-zinc-100 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-[0.06em] text-zinc-400 mb-1">Q{q.order}: {q.text?.slice(0, 40)} &rarr; {opt.label}</div>
                    {opt.output_items.map((item, ii) => (
                      <div key={ii} className="flex items-center gap-2 text-xs text-zinc-600 py-0.5">
                        <span className={`px-1.5 py-0.5 rounded-sm text-[9px] ${item.type === 'heading' ? 'bg-purple-50 text-purple-600' : item.type === 'tick' ? 'bg-blue-50 text-blue-600' : item.type === 'note' ? 'bg-amber-50 text-amber-600' : 'bg-zinc-100 text-zinc-500'}`}>
                          {item.type}
                        </span>
                        <span>{item.text}</span>
                        {item.section_group && <span className="text-zinc-300 ml-auto">[{item.section_group}]</span>}
                      </div>
                    ))}
                  </div>
                )))}
              </div>
            )}
          </div>
        )}

        {tab === 'project_fields' && (
          <div className="p-5 overflow-y-auto h-full">
            <h2 className="text-sm font-medium text-zinc-900 mb-3">Project fields</h2>
            <p className="text-xs text-zinc-400 mb-3">Fields collected from staff when starting a run</p>
            <div className="space-y-2">
              {(template.project_fields || []).map((f, idx) => {
                const isDefault = ['client_name', 'project_name', 'engineer_name'].includes(f.key);
                return (
                  <div key={f.key} className="flex items-center gap-3 bg-white border border-zinc-100 rounded-sm p-3">
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-[10px] text-zinc-400 mb-0.5">Label</label>
                        <input value={f.label} onChange={e => updateProjectField(idx, { label: e.target.value })}
                          className="w-full px-2 py-1 text-xs border border-zinc-200 rounded-sm" disabled={isDefault} />
                      </div>
                      <div>
                        <label className="block text-[10px] text-zinc-400 mb-0.5">Key</label>
                        <input value={f.key} className="w-full px-2 py-1 text-xs border border-zinc-200 rounded-sm bg-zinc-50" disabled />
                      </div>
                      <div>
                        <label className="block text-[10px] text-zinc-400 mb-0.5">Placeholder</label>
                        <input value={f.placeholder || ''} onChange={e => updateProjectField(idx, { placeholder: e.target.value })}
                          className="w-full px-2 py-1 text-xs border border-zinc-200 rounded-sm" />
                      </div>
                    </div>
                    <label className="flex items-center gap-1 text-[10px] text-zinc-400">
                      <input type="checkbox" checked={f.required} onChange={e => updateProjectField(idx, { required: e.target.checked })} disabled={isDefault} />
                      Required
                    </label>
                    {!isDefault && (
                      <button onClick={() => deleteProjectField(idx)} className="text-zinc-300 hover:text-red-500"><Trash2 size={12} /></button>
                    )}
                  </div>
                );
              })}
            </div>
            <button onClick={addProjectField} className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-600 mt-3">
              <Plus size={12} /> Add custom field
            </button>
          </div>
        )}
      </div>
      {showPreview && <TemplatePreview template={template} onClose={() => setShowPreview(false)} />}
    </div>
  );
}
